import type { ViewApi } from '@fullcalendar/core';
import type FullCalendar from '@fullcalendar/react';
import { getServerErrorMessage } from '@src/shared/lib';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { getInitialVisibleRange, getSessionId } from '../../lib';
import type {
  AbsenceLockState,
  SelectedAbsence,
  TimelineSlot,
  VisibleRange,
} from '../../types';
import {
  ABSENCE_LOCK_STATUS,
  isAbsenceLockErrorStatus,
  isAbsenceLockHeldStatus,
} from '../../types';
import { TimelineContext } from '../context';
import { useAbsenceLock } from '../query';

const initialLockState: AbsenceLockState = {
  status: ABSENCE_LOCK_STATUS.IDLE,
  lock: null,
  message: null,
};
const ABSENCE_LOCK_RETRY_INTERVAL_MS = 2000;

export default function TimelineProvider({
  children,
}: {
  children: React.ReactNode;
}) {
  const [view, setView] = useState<ViewApi | null>(null);
  const [selectedAbsence, setSelectedAbsence] =
    useState<SelectedAbsence | null>(null);
  const [selectedSlot, setSelectedSlot] = useState<TimelineSlot | null>(null);
  const [selectedUserId, setSelectedUserId] = useState<number | null>(null);
  const [sidebarMode, setSidebarMode] = useState<'schedule' | 'details' | null>(
    null,
  );
  const [suppressSelectionUntil, setSuppressSelectionUntil] = useState(0);
  const [visibleRange, setVisibleRange] = useState<VisibleRange>(
    getInitialVisibleRange,
  );
  const [absenceLock, setAbsenceLock] =
    useState<AbsenceLockState>(initialLockState);
  const [showLegend, setShowLegend] = useState(false);
  const [showBalanceHistory, setShowBalanceHistory] = useState(false);
  const calendarRef = useRef<FullCalendar | null>(null);
  const { acquireLock, refreshLock, releaseLock } = useAbsenceLock();
  const lockRef = useRef(absenceLock);
  const activeLockTargetUserId =
    sidebarMode === 'schedule'
      ? selectedSlot?.userId ?? null
      : sidebarMode === 'details'
        ? selectedAbsence?.userId ?? null
        : null;
  const previousUserIdRef = useRef<number | null>(activeLockTargetUserId);

  useEffect(() => {
    lockRef.current = absenceLock;
  }, [absenceLock]);

  const setIdleLockState = useCallback(() => {
    setAbsenceLock(initialLockState);
  }, []);

  const releaseCurrentLock = useCallback(
    async (lockedUserId?: number | null) => {
      const lock = lockRef.current.lock;
      if (!lock) {
        setIdleLockState();
        return;
      }
      if (lockedUserId != null && lock.userId !== lockedUserId) {
        return;
      }

      try {
        await releaseLock({
          acti_user: lock.userId,
          session_id: getSessionId(),
        });
      } catch {
      } finally {
        if (
          lockedUserId == null ||
          lockRef.current.lock?.userId === lockedUserId
        ) {
          setIdleLockState();
        }
      }
    },
    [releaseLock, setIdleLockState],
  );

  useEffect(() => {
    const nextUserId = activeLockTargetUserId;
    const previousUserId = previousUserIdRef.current;
    const currentLock = lockRef.current.lock;
    const currentStatus = lockRef.current.status;

    if (
      nextUserId === previousUserId &&
      (nextUserId === null ||
        currentStatus === ABSENCE_LOCK_STATUS.ACQUIRING ||
        isAbsenceLockErrorStatus(currentStatus) ||
        (isAbsenceLockHeldStatus(currentStatus) &&
          currentLock?.userId === nextUserId))
    ) {
      return;
    }

    previousUserIdRef.current = nextUserId;
    let isCancelled = false;

    const syncLock = async () => {
      if (currentLock && currentLock.userId !== nextUserId) {
        await releaseCurrentLock(currentLock.userId);
      }

      if (isCancelled) {
        return;
      }

      if (!nextUserId) {
        setIdleLockState();
        return;
      }

      if (
        lockRef.current.lock?.userId === nextUserId &&
        isAbsenceLockHeldStatus(lockRef.current.status)
      ) {
        return;
      }

      setAbsenceLock({
        status: ABSENCE_LOCK_STATUS.ACQUIRING,
        lock: null,
        message: 'Securing this user for editing...',
      });

      try {
        const lock = await acquireLock({
          acti_user: nextUserId,
          session_id: getSessionId(),
        });
        if (isCancelled) {
          await releaseLock({
            acti_user: lock.userId,
            session_id: getSessionId(),
          }).catch(() => undefined);
          return;
        }
        setAbsenceLock({
          status: ABSENCE_LOCK_STATUS.LOCKED,
          lock,
          message: null,
        });
      } catch (error) {
        if (isCancelled) {
          return;
        }
        const baseMessage = getServerErrorMessage(error);
        const lockedBy =
          error &&
          typeof error === 'object' &&
          'response' in error &&
          (error as { response?: { data?: { locked_by?: unknown } } }).response
            ?.data?.locked_by;
        const message =
          typeof lockedBy === 'string' && lockedBy
            ? `${baseMessage} \n Locked by: ${lockedBy}.`
            : baseMessage;
        setAbsenceLock({
          status: ABSENCE_LOCK_STATUS.CONFLICTED,
          lock: null,
          message,
        });
      }
    };

    void syncLock();

    return () => {
      isCancelled = true;
    };
  }, [
    acquireLock,
    activeLockTargetUserId,
    releaseCurrentLock,
    releaseLock,
    setIdleLockState,
  ]);

  useEffect(() => {
    const lock = absenceLock.lock;
    if (!isAbsenceLockHeldStatus(absenceLock.status) || !lock) {
      return undefined;
    }

    const timer = window.setInterval(() => {
      void refreshLock({
        acti_user: lock.userId,
        session_id: getSessionId(),
      })
        .then((refreshedLock) => {
          setAbsenceLock((current) => {
            if (current.lock?.userId !== lock.userId) {
              return current;
            }
            return {
              status: ABSENCE_LOCK_STATUS.LOCKED,
              lock: refreshedLock,
              message: null,
            };
          });
        })
        .catch((error) => {
          setAbsenceLock((current) => {
            if (current.lock?.userId !== lock.userId) {
              return current;
            }
            return {
              status: ABSENCE_LOCK_STATUS.EXPIRED,
              lock: null,
              message: getServerErrorMessage(error),
            };
          });
        });
    }, lock.heartbeatIntervalSeconds * 1000);

    return () => {
      window.clearInterval(timer);
    };
  }, [absenceLock.lock, absenceLock.status, refreshLock]);

  useEffect(() => {
    if (
      !activeLockTargetUserId ||
      !isAbsenceLockErrorStatus(absenceLock.status) ||
      lockRef.current.lock
    ) {
      return undefined;
    }

    const retryUserId = activeLockTargetUserId;

    const retryAcquireLock = () => {
      void acquireLock({
        acti_user: retryUserId,
        session_id: getSessionId(),
      })
        .then((lock) => {
          setAbsenceLock((current) => {
            if (
              !isAbsenceLockErrorStatus(current.status) ||
              lock.userId !== retryUserId
            ) {
              return current;
            }
            return {
              status: ABSENCE_LOCK_STATUS.LOCKED,
              lock,
              message: null,
            };
          });
        })
        .catch((error) => {
          setAbsenceLock((current) => {
            if (
              !isAbsenceLockErrorStatus(current.status) ||
              lockRef.current.lock?.userId === retryUserId
            ) {
              return current;
            }
            const baseMessage = getServerErrorMessage(error);
            const lockedBy =
              error &&
              typeof error === 'object' &&
              'response' in error &&
              (error as { response?: { data?: { locked_by?: unknown } } })
                .response?.data?.locked_by;
            const message =
              typeof lockedBy === 'string' && lockedBy
                ? `${baseMessage} \n Locked by: ${lockedBy}.`
                : baseMessage;
            return {
              status: current.status,
              lock: null,
              message,
            };
          });
        });
    };

    const timer = window.setInterval(
      retryAcquireLock,
      ABSENCE_LOCK_RETRY_INTERVAL_MS,
    );

    return () => {
      window.clearInterval(timer);
    };
  }, [absenceLock.status, acquireLock, activeLockTargetUserId]);

  useEffect(() => {
    return () => {
      const lock = lockRef.current.lock;
      if (!lock) {
        return;
      }
      void releaseLock({
        acti_user: lock.userId,
        session_id: getSessionId(),
      }).catch(() => undefined);
    };
  }, [releaseLock]);

  useEffect(() => {
    if (!showBalanceHistory) {
      return;
    }

    if (selectedAbsence || selectedSlot || sidebarMode || showLegend) {
      setShowBalanceHistory(false);
    }
  }, [
    selectedAbsence,
    selectedSlot,
    showBalanceHistory,
    showLegend,
    sidebarMode,
  ]);

  const closeEditForm = useCallback(async () => {
    const lockedUserId = lockRef.current.lock?.userId ?? null;
    await releaseCurrentLock(lockedUserId);
    previousUserIdRef.current = null;
    setSidebarMode(null);
    setSelectedAbsence(null);
    setSelectedSlot(null);
  }, [releaseCurrentLock]);

  const closeAbsenceDetails = useCallback(async () => {
    const lockedUserId = lockRef.current.lock?.userId ?? null;
    await releaseCurrentLock(lockedUserId);
    previousUserIdRef.current = null;
    setSidebarMode(null);
    setSelectedAbsence(null);
    setSelectedSlot(null);
  }, [releaseCurrentLock]);

  const openLegend = useCallback(() => setShowLegend(true), []);
  const closeLegend = useCallback(() => setShowLegend(false), []);
  const openBalanceHistory = useCallback(() => setShowBalanceHistory(true), []);
  const closeBalanceHistory = useCallback(
    () => setShowBalanceHistory(false),
    [],
  );

  const value = useMemo(
    () => ({
      view,
      setView,
      selectedAbsence,
      setSelectedAbsence,
      selectedSlot,
      setSelectedSlot,
      selectedUserId,
      setSelectedUserId,
      sidebarMode,
      setSidebarMode,
      suppressSelectionUntil,
      setSuppressSelectionUntil,
      closeEditForm,
      calendarRef,
      visibleRange,
      setVisibleRange,
      absenceLock,
      setAbsenceLock,
      showLegend,
      openLegend,
      closeLegend,
      showBalanceHistory,
      openBalanceHistory,
      closeBalanceHistory,
      closeAbsenceDetails,
    }),
    [
      absenceLock,
      closeEditForm,
      view,
      selectedAbsence,
      selectedSlot,
      selectedUserId,
      sidebarMode,
      suppressSelectionUntil,
      visibleRange,
      showLegend,
      openLegend,
      closeLegend,
      showBalanceHistory,
      openBalanceHistory,
      closeBalanceHistory,
      closeAbsenceDetails,
    ],
  );

  return <TimelineContext value={value}>{children}</TimelineContext>;
}
